import * as actionTypes from "../actions/actionTypes";

// Define interface for calculated interest
interface CalculatedInterest {
  interest_calculated: string;
  maturity_amount: string;
  monthly_interest_payable: string;
  monthly_interest_accruals: any[];
}

// Define interface for selected investor
interface SelectedInvestor {
  value: number;
  label: string;
  wallets: any[];
}

// Define interface for transaction receipt
interface TransactionReceipt {
  base64: string;
  fileName: string;
}

// Define interface for wallet data
interface Wallet {
  id: number;
  accountName: string;
  legalEntity: string;
  walletId: string;
  currency: string;
  availableBalance: number;
  ledgerBalance: number;
  accountNumber: string;
  clientId: string;
  isExternal: boolean;
  isDefault: boolean;
  externalAccountNumber: string;
  walletProviderType: string;
}

export interface InvestmentFormState {
  formData: {
    funder_id: number | null;
    raw_funder_id: number | null;
    investment_product_id: number | null;
    principal_amount: number;
    amount_to_credit: number;
    interest_rate: number;
    tenure: number;
    currency: string;
    interest_repayment_type: string;
    number_of_days_in_year: number;
    effective_date: string;
    transactionReceiptBase64: string;
    transactionReceiptName: string;
    wallet_id: string;
    wallet_balance: number;
    wallet_balance_backup: number;
    relationship_manager_id: number | null;
    /** Dynamic deposits only — book the typed rate, not the chart's. */
    override_chart_interest: boolean;
  };
  walletBalance: number;
  currentStep: number;
  isWalletBalanceLoading: boolean;
  walletBalanceError: string | null;
  calculatedInterest: CalculatedInterest;
  isCalculatingInterest: boolean;
  calculateInterestError: string | null;
  showBreakdownModal: boolean;
  transactionReceipt: TransactionReceipt;
  selectedInvestor: SelectedInvestor | null;
  isLoading: boolean;
  availableWallets: Wallet[];
}

const initialState: InvestmentFormState = {
  formData: {
    funder_id: null,
    raw_funder_id: null,
    investment_product_id: null,
    principal_amount: 0,
    amount_to_credit: 0,
    interest_rate: 0,
    tenure: 0,
    currency: "",
    interest_repayment_type: "",
    number_of_days_in_year: 365,
    effective_date: "",
    transactionReceiptBase64: "",
    transactionReceiptName: "",
    wallet_id: "",
    wallet_balance: 0,
    wallet_balance_backup: 0,
    relationship_manager_id: null,
    override_chart_interest: false,
  },
  walletBalance: 0,
  currentStep: 0,
  isWalletBalanceLoading: false,
  walletBalanceError: null,
  calculatedInterest: {
    interest_calculated: "",
    maturity_amount: "",
    monthly_interest_payable: "",
    monthly_interest_accruals: [],
  },
  isCalculatingInterest: false,
  calculateInterestError: null,
  showBreakdownModal: false,
  transactionReceipt: {
    base64: "",
    fileName: "",
  },
  selectedInvestor: null,
  isLoading: false,
  availableWallets: [],
};

const reducer = (state = initialState, action: any) => {
  switch (action.type) {
    case actionTypes.INVESTMENT_FORM_UPDATE_DATA:
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload,
        },
      };

    case actionTypes.INVESTMENT_FORM_SET_WALLET_BALANCE:
      return {
        ...state,
        walletBalance: action.payload,
        formData: {
          ...state.formData,
          wallet_balance: action.payload,
          wallet_balance_backup: action.payload,
        },
      };

    case actionTypes.INVESTMENT_FORM_SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload,
      };

    case actionTypes.INVESTMENT_FORM_RESET:
      return initialState;

    case actionTypes.INVESTMENT_FORM_FETCH_WALLET_BALANCE_START:
      return {
        ...state,
        isWalletBalanceLoading: true,
        walletBalanceError: null,
      };

    case actionTypes.INVESTMENT_FORM_FETCH_WALLET_BALANCE_SUCCESS:
      return {
        ...state,
        isWalletBalanceLoading: false,
        walletBalanceError: null,
        walletBalance: action.payload.availableBalance,
        formData: {
          ...state.formData,
          wallet_balance: action.payload.availableBalance,
          wallet_balance_backup: action.payload.availableBalance,
        },
      };

    case actionTypes.INVESTMENT_FORM_FETCH_WALLET_BALANCE_FAILURE:
      return {
        ...state,
        isWalletBalanceLoading: false,
        walletBalanceError: action.payload,
      };

    case actionTypes.INVESTMENT_FORM_SET_CALCULATED_INTEREST:
      return {
        ...state,
        calculatedInterest: action.payload,
      };

    case actionTypes.INVESTMENT_FORM_CALCULATE_INTEREST_START:
      return {
        ...state,
        isCalculatingInterest: true,
        calculateInterestError: null,
      };

    case actionTypes.INVESTMENT_FORM_CALCULATE_INTEREST_SUCCESS:
      return {
        ...state,
        isCalculatingInterest: false,
        calculateInterestError: null,
        calculatedInterest: action.payload,
      };

    case actionTypes.INVESTMENT_FORM_CALCULATE_INTEREST_FAILURE:
      return {
        ...state,
        isCalculatingInterest: false,
        calculateInterestError: action.payload,
      };

    case actionTypes.INVESTMENT_FORM_TOGGLE_BREAKDOWN_MODAL:
      return {
        ...state,
        showBreakdownModal:
          action.payload !== undefined
            ? action.payload
            : !state.showBreakdownModal,
      };

    case actionTypes.INVESTMENT_FORM_SET_TRANSACTION_RECEIPT:
      return {
        ...state,
        transactionReceipt: action.payload,
        formData: {
          ...state.formData,
          transactionReceiptBase64: action.payload.base64,
          transactionReceiptName: action.payload.fileName,
        },
      };

    case actionTypes.INVESTMENT_FORM_SET_SELECTED_INVESTOR:
      return {
        ...state,
        selectedInvestor: action.payload,
      };

    case actionTypes.INVESTMENT_FORM_SET_AVAILABLE_WALLETS:
      return {
        ...state,
        availableWallets: action.payload,
      };

    default:
      return state;
  }
};

export default reducer;
